
import React, { useState, useEffect } from 'react';
import { User, Phone, IdCard, CheckCircle2, Loader2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import axiosInstance from '@/utils/axiosInstance';
import { API_ENDPOINTS } from '@/constants/config';

const Step7Driver = ({ wizardData, updateWizardData }) => {
  const [availableDrivers, setAvailableDrivers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!wizardData.useDefaultDriver) {
      fetchDrivers();
    }
  }, [wizardData.useDefaultDriver]);

  const fetchDrivers = async () => {
    try {
      setLoading(true);
      // Mock drivers for demonstration
      const mockDrivers = [
        {
          id: '1',
          name: 'Michael Brown',
          phone: '+1 (555) 123-4567',
          license: 'DL-123456',
          experience: '5 years',
          status: 'available',
        },
        {
          id: '2',
          name: 'Sarah Davis',
          phone: '+1 (555) 234-5678',
          license: 'DL-234567',
          experience: '7 years',
          status: 'available',
        },
        {
          id: '3',
          name: 'Robert Wilson',
          phone: '+1 (555) 345-6789',
          license: 'DL-345678',
          experience: '3 years',
          status: 'available',
        },
      ];
      setAvailableDrivers(mockDrivers);
    } catch (err) {
      console.error('Error fetching drivers:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleDefaultDriver = (checked) => {
    updateWizardData('useDefaultDriver', checked);
    if (checked) {
      updateWizardData('driver', null);
    }
  };

  const handleSelectDriver = (driver) => {
    updateWizardData('driver', driver);
  };

  // Default driver from destination bus
  const defaultDriver = {
    name: wizardData.destinationBus?.driver || 'Default Driver',
    phone: '+1 (555) 000-0000',
    license: 'DL-000000',
    experience: '5 years',
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="text-gray-600 mb-2">
          Choose who will drive the merged bus. You can use the default driver assigned to{' '}
          <strong>{wizardData.destinationBus?.busNumber}</strong> or select a different driver.
        </p>
      </div>

      {/* Toggle Default Driver */}
      <Card className="p-6">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <Label htmlFor="use-default" className="text-base font-semibold text-gray-900">
              Use Default Driver
            </Label>
            <p className="text-sm text-gray-600 mt-1">
              Continue with the driver currently assigned to {wizardData.destinationBus?.busNumber}
            </p>
          </div>
          <Switch
            id="use-default"
            checked={wizardData.useDefaultDriver}
            onCheckedChange={handleToggleDefaultDriver}
          />
        </div>
      </Card>

      {/* Default Driver Info */}
      {wizardData.useDefaultDriver && (
        <Card className="p-6 bg-blue-50 border-2 border-blue-600">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
              <User className="h-8 w-8 text-blue-600" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-3">
                <h3 className="text-lg font-semibold text-gray-900">{defaultDriver.name}</h3>
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                  Selected
                </span>
              </div>
              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  <span>{defaultDriver.phone}</span>
                </div>
                <div className="flex items-center gap-2">
                  <IdCard className="h-4 w-4" />
                  <span>License: {defaultDriver.license}</span>
                </div>
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  <span>Experience: {defaultDriver.experience}</span>
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Available Drivers */}
      {!wizardData.useDefaultDriver && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">Select a Different Driver</h3>
          
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              <span className="ml-3 text-gray-600">Loading drivers...</span>
            </div>
          ) : availableDrivers.length === 0 ? (
            <Card className="p-8 text-center">
              <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No other drivers available</p>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {availableDrivers.map((driver) => {
                const isSelected = wizardData.driver?.id === driver.id;
                
                return (
                  <Card
                    key={driver.id}
                    onClick={() => handleSelectDriver(driver)}
                    className={`p-6 cursor-pointer transition-all hover:shadow-md ${
                      isSelected
                        ? 'border-2 border-blue-600 bg-blue-50'
                        : 'border border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
                        <User className="h-6 w-6 text-gray-600" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-3">
                          <h4 className="font-semibold text-gray-900">{driver.name}</h4>
                          {isSelected && (
                            <CheckCircle2 className="h-5 w-5 text-blue-600" />
                          )}
                        </div>
                        <div className="space-y-2 text-sm text-gray-600">
                          <div className="flex items-center gap-2">
                            <Phone className="h-3 w-3" />
                            <span>{driver.phone}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <IdCard className="h-3 w-3" />
                            <span>License: {driver.license}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <User className="h-3 w-3" />
                            <span>Experience: {driver.experience}</span>
                          </div>
                        </div>
                        <div className="mt-3">
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              driver.status === 'available'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            <span
                              className={`w-1.5 h-1.5 rounded-full mr-1 ${
                                driver.status === 'available' ? 'bg-green-500' : 'bg-gray-500'
                              }`}
                            />
                            {driver.status === 'available' ? 'Available' : 'Busy'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Summary */}
      <Card className="p-4 bg-green-50 border-green-200">
        <h4 className="font-semibold text-gray-900 mb-2">Driver Assignment</h4>
        <p className="text-sm text-gray-700">
          {wizardData.useDefaultDriver ? (
            <>
              <strong>{defaultDriver.name}</strong> will continue as the driver for the merged bus{' '}
              <strong>{wizardData.destinationBus?.busNumber}</strong>.
            </>
          ) : wizardData.driver ? (
            <>
              <strong>{wizardData.driver.name}</strong> has been assigned as the driver for the
              merged bus <strong>{wizardData.destinationBus?.busNumber}</strong>.
            </>
          ) : (
            'Please select a driver to proceed.'
          )}
        </p>
      </Card>
    </div>
  );
};

export default Step7Driver;
